import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  // Reply as ReplyIcon,
  // Forward as ForwardIcon,
  Star as StarIcon,
  PriorityHigh as PriorityIcon,
  AttachFile as AttachIcon,
  PersonAdd as PersonAddIcon,
  // OpenInNew as OpenInNewIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
  Visibility as PreviewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useGmail } from '../../hooks/useGmail';
import { useDatabase } from '../../hooks/useDatabase';
import { EmailMetadata, EmailDetailData } from '../../types/gmail';

interface EmailDetailProps {
  open: boolean;
  onClose: () => void;
  email: EmailMetadata | null;
  onReply: (email: EmailMetadata) => void;
  onForward?: (email: EmailMetadata, emailDetail?: EmailDetailData | null) => void;
  onAddContact: (email: string, name: string) => void | Promise<void>;
  onSyncToEvent?: (emailId: string, contactId?: string) => void;
}

export function EmailDetail({
  open,
  onClose,
  email,
  onReply,
  onForward,
  onAddContact,
  onSyncToEvent
}: EmailDetailProps) {
  const { loading, error, getEmail } = useGmail();
  const db = useDatabase();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [emailDetail, setEmailDetail] = useState<EmailDetailData | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<{
    filename: string;
    mimeType: string;
    data: string;
  } | null>(null);
  const [contactStatus, setContactStatus] = useState<'unknown' | 'exists' | 'added' | 'adding'>('unknown');
  const [contactId, setContactId] = useState<string | null>(null);

  const loadEmailDetail = useCallback(async () => {
    if (!email) return;
    
    const detail = await getEmail(email.id);
    setEmailDetail(detail);
  }, [email, getEmail]);

  // Load email details when email changes
  useEffect(() => {
    if (open && email) {
      loadEmailDetail();
    } else {
      setEmailDetail(null);
    }
  }, [open, email, loadEmailDetail]);

  // const handleReply = () => {
  //   if (email) {
  //     onReply(email);
  //   }
  // };

  // const handleForward = () => {
  //   if (email && onForward) {
  //     // Pass both email metadata and full content if available
  //     onForward(email, emailDetail);
  //   }
  // };

  const handleAddContact = async () => {
    if (email) {
      const emailAddress = extractEmailAddress(email.from);
      const nameMatch = email.from.match(/^([^<]+)/);
      const name = nameMatch ? nameMatch[1].trim() : emailAddress;
      
      // Only call onAddContact if contact doesn't exist
      if (contactStatus !== 'exists' && contactStatus !== 'added') {
        setContactStatus('adding');
        try {
          const result = onAddContact(emailAddress, name);
          // Handle both Promise and void return types
          if (result instanceof Promise) {
            await result;
          }
          // Re-check contact status after adding (with a small delay to allow DB to update)
          setTimeout(() => {
            checkContactExists(emailAddress);
          }, 1000);
        } catch (err) {
          console.error('Error adding contact:', err);
          setContactStatus('unknown');
        }
      }
    }
  };



  const handlePreviewAttachment = async (attachment: any) => {
    if (!email) return;
    
    try {
      const response = await fetch('/.netlify/functions/gmail-download-attachment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: localStorage.getItem('userId'),
          emailId: email.id,
          attachmentId: attachment.attachmentId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to load attachment');
      }

      const result = await response.json();
      
      // Create data URL from base64 data
      const dataUrl = `data:${attachment.mimeType};base64,${result.data}`;
      
      setPreviewAttachment({
        filename: attachment.filename,
        mimeType: attachment.mimeType,
        data: dataUrl
      });
    } catch (error) {
      console.error('Error loading attachment:', error);
      alert('Failed to load attachment for preview');
    }
  };

  const getAttachmentIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <ImageIcon />;
    if (mimeType === 'application/pdf') return <PdfIcon />;
    if (mimeType.includes('text/') || mimeType.includes('document')) return <DocumentIcon />;
    return <AttachIcon />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const extractEmailFromHeader = (header: string) => {
    const emailMatch = header.match(/<([^>]+)>/);
    return emailMatch ? emailMatch[1] : header;
  };

  const extractNameFromHeader = (header: string) => {
    const nameMatch = header.match(/^([^<]+)/);
    return nameMatch ? nameMatch[1].trim().replace(/"/g, '') : header;
  };

  // Extract email address from "Name <email>" format
  const extractEmailAddress = useCallback((fromString: string) => {
    const match = fromString.match(/<([^>]+)>/);
    if (match) {
      return match[1];
    }
    return fromString;
  }, []);

  // Check if contact exists
  const checkContactExists = useCallback(async (emailAddress: string) => {
    try {
      const result = await db.getContactByEmail(emailAddress);
      if (result.data) {
        setContactStatus('exists');
        setContactId(result.data.id);
      } else {
        setContactStatus('unknown');
        setContactId(null);
      }
    } catch (err) {
      console.error('Error checking contact:', err);
      setContactStatus('unknown');
      setContactId(null);
    }
  }, [db]);

  // Check contact status when email changes
  useEffect(() => {
    if (open && email) {
      const emailAddress = extractEmailAddress(email.from);
      checkContactExists(emailAddress);
    } else {
      setContactStatus('unknown');
      setContactId(null);
    }
  }, [open, email, extractEmailAddress, checkContactExists]);

  const handleViewContact = () => {
    if (contactId) {
      window.open(`/contacts/${contactId}`, '_blank');
    }
  };

  const decodeHtmlEntities = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const cleanEmailContent = (content: string) => {
    // Remove common image file extensions and patterns from email content
    return content
      .replace(/image\d+\.(jpeg|jpg|png|gif|bmp|webp)/gi, '')
      .replace(/attachment\d*\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|zip|rar)/gi, '')
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
  };

  const sanitizeHtml = (html: string) => {
    // Decode HTML entities first
    const decodedHtml = decodeHtmlEntities(html);
    
    // Clean the content
    const cleanedHtml = cleanEmailContent(decodedHtml);
    
    // Basic HTML sanitization - in production, use a proper sanitization library
    return cleanedHtml
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '');
  };

  if (!email) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { 
          height: isMobile ? '100vh' : '90vh',
          m: isMobile ? 0 : undefined
        }
      }}
    >
      <DialogTitle sx={{ p: isMobile ? 2 : 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0
        }}>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            sx={{ 
              flexGrow: 1, 
              mr: isMobile ? 0 : 2,
              fontSize: isMobile ? '1rem' : undefined,
              lineHeight: isMobile ? 1.3 : undefined
            }}
          >
            {email.subject}
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 0.5 : 1,
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}>
            {email.isImportant && (
              <Tooltip title="Important">
                <PriorityIcon color="warning" fontSize={isMobile ? "small" : "medium"} />
              </Tooltip>
            )}
            
            {email.isStarred && (
              <Tooltip title="Starred">
                <StarIcon color="warning" fontSize={isMobile ? "small" : "medium"} />
              </Tooltip>
            )}
            
            {email.hasAttachments && (
              <Tooltip title="Has Attachments">
                <AttachIcon color="action" fontSize={isMobile ? "small" : "medium"} />
              </Tooltip>
            )}

            <IconButton onClick={onClose} size={isMobile ? "small" : "medium"}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: isMobile ? 2 : 3 }}>
        {loading && !emailDetail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box>
            {/* Email Header */}
            <Paper sx={{ p: isMobile ? 1.5 : 2, mb: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                gap: 2, 
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: isMobile ? 32 : 40, height: isMobile ? 32 : 40 }}>
                    {extractNameFromHeader(email.from).charAt(0).toUpperCase()}
                  </Avatar>
                  
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant={isMobile ? "body1" : "subtitle1"} sx={{ fontWeight: 600 }}>
                      {extractNameFromHeader(email.from)}
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                      {extractEmailFromHeader(email.from)}
                    </Typography>
                  </Box>

                  {!isMobile && (
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(email.date)}
                    </Typography>
                  )}
                </Box>

                {isMobile && (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'flex-end' }}>
                    {formatDate(email.date)}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  <Chip label="From" size="small" variant="outlined" />
                  <Typography variant={isMobile ? "caption" : "body2"} sx={{ wordBreak: 'break-all' }}>
                    {email.from}
                  </Typography>
                </Box>

                {email.to && email.to.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Chip label="To" size="small" variant="outlined" />
                    <Typography variant={isMobile ? "caption" : "body2"} sx={{ wordBreak: 'break-all' }}>
                      {Array.isArray(email.to) ? email.to.join(', ') : email.to}
                    </Typography>
                  </Box>
                )}

                {email.cc && email.cc.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Chip label="CC" size="small" variant="outlined" />
                    <Typography variant={isMobile ? "caption" : "body2"} sx={{ wordBreak: 'break-all' }}>
                      {Array.isArray(email.cc) ? email.cc.join(', ') : email.cc}
                    </Typography>
                  </Box>
                )}
              </Box>

              {email.labels && email.labels.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {email.labels.map((label, index) => (
                    <Chip
                      key={index}
                      label={label}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Paper>

            {/* Email Content */}
            {emailDetail?.content && (
              <Box>
                {emailDetail.content.html ? (
                  <Box
                    sx={{
                      '& img': { maxWidth: '100%', height: 'auto' },
                      '& a': { color: 'primary.main', textDecoration: 'none' },
                      '& a:hover': { textDecoration: 'underline' },
                      fontSize: isMobile ? '0.9rem' : undefined,
                      lineHeight: isMobile ? 1.4 : undefined
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(emailDetail.content.html)
                    }}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: isMobile ? '0.9rem' : undefined,
                      lineHeight: isMobile ? 1.4 : undefined
                    }}
                  >
                    {cleanEmailContent(emailDetail.content.text || '')}
                  </Typography>
                )}
              </Box>
            )}

            {/* Attachments */}
            {emailDetail?.content.attachments && emailDetail.content.attachments.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Attachments ({emailDetail.content.attachments.length})
                </Typography>
                <List>
                  {emailDetail.content.attachments.map((attachment, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        {getAttachmentIcon(attachment.mimeType)}
                      </ListItemIcon>
                      <ListItemText
                        primary={attachment.filename}
                        secondary={`${attachment.mimeType} (${Math.round(attachment.size / 1024)} KB)`}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Preview attachment">
                          <IconButton 
                            size="small"
                            onClick={() => handlePreviewAttachment(attachment)}
                          >
                            <PreviewIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: isMobile ? 2 : 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        '& > *': {
          width: isMobile ? '100%' : 'auto'
        }
      }}>
        {isMobile ? (
          <>
            {/* <Button
              variant="contained"
              startIcon={<ReplyIcon />}
              onClick={handleReply}
              size="large"
            >
              Reply
            </Button> */}
            {contactStatus === 'exists' || contactStatus === 'added' ? (
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={handleViewContact}
                sx={{ color: 'success.main', borderColor: 'success.main' }}
              >
                View Contact
              </Button>
            ) : (
              <Button
                variant="outlined"
                startIcon={contactStatus === 'adding' ? <CircularProgress size={16} /> : <PersonAddIcon />}
                onClick={handleAddContact}
                disabled={contactStatus === 'adding'}
              >
                {contactStatus === 'adding' ? 'Adding...' : 'Add Contact'}
              </Button>
            )}
            {/* <Button
              variant="outlined"
              startIcon={<ForwardIcon />}
              onClick={handleForward}
              disabled={!onForward}
            >
              Forward
            </Button> */}
          </>
        ) : (
          <>
            {contactStatus === 'exists' || contactStatus === 'added' ? (
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={handleViewContact}
                sx={{ color: 'success.main', borderColor: 'success.main' }}
              >
                View Contact
              </Button>
            ) : (
              <Button
                variant="outlined"
                startIcon={contactStatus === 'adding' ? <CircularProgress size={16} /> : <PersonAddIcon />}
                onClick={handleAddContact}
                disabled={contactStatus === 'adding'}
              >
                {contactStatus === 'adding' ? 'Adding...' : 'Add Contact'}
              </Button>
            )}

            {/* <Button
              variant="outlined"
              startIcon={<ReplyIcon />}
              onClick={handleReply}
            >
              Reply
            </Button> */}

            {/* <Button
              variant="outlined"
              startIcon={<ForwardIcon />}
              onClick={handleForward}
              disabled={!onForward}
            >
              Forward
            </Button> */}

            <Button onClick={onClose}>
              Close
            </Button>
          </>
        )}
      </DialogActions>

      {/* Attachment Preview Dialog */}
      <Dialog
        open={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {previewAttachment?.filename}
            </Typography>
            <IconButton onClick={() => setPreviewAttachment(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewAttachment && (
            <Box sx={{ textAlign: 'center' }}>
              {previewAttachment.mimeType.startsWith('image/') ? (
                <img
                  src={previewAttachment.data}
                  alt={previewAttachment.filename}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain'
                  }}
                />
              ) : previewAttachment.mimeType === 'application/pdf' ? (
                <iframe
                  src={previewAttachment.data}
                  width="100%"
                  height="600px"
                  style={{ border: 'none' }}
                  title={previewAttachment.filename}
                />
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Preview not available for this file type
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewAttachment(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
